import { useState, useEffect } from 'react'
import { Link, useSearchParams, useLocation } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, Mail, MessageSquare, Image, Clock, CheckCircle, XCircle, Trash2 } from 'lucide-react'
import { api } from '../utils/api'
import { format } from 'date-fns'

export default function Messages() {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location])
  
  const status = searchParams.get('status') || ''
  const type = searchParams.get('type') || ''
  
  const { data: messages, isLoading } = useQuery({
    queryKey: ['messages', status, type],
    queryFn: async () => {
      const result = await api.messages.list({ status: status || undefined, type: type || undefined })
      console.log('Messages API result:', result)
      return result
    },
  })
  
  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.messages.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] })
    },
  })
  
  const typeIcons = {
    email: Mail,
    sms: MessageSquare,
    mms: Image,
  }
  
  const statusIcons = {
    pending: Clock,
    sent: CheckCircle,
    failed: XCircle,
  }

  function getScheduleDescription(msg: any): string {
    switch (msg.schedule_type) {
      case 'beat':
        return `Every ${msg.beat_interval_hours}h`
      case 'onetime':
        return msg.scheduled_at 
          ? format(new Date(msg.scheduled_at), 'MMM d, yyyy h:mm a')
          : 'Not scheduled'
      case 'recurring':
        const pattern = msg.recurring_pattern === 'daily' ? 'Daily' 
                     : msg.recurring_pattern === 'weekly' ? 'Weekly' 
                     : 'Monthly'
        const start = msg.recurring_start 
          ? format(new Date(msg.recurring_start), 'MMM d, yyyy h:mm a')
          : ''
        const end = msg.recurring_end 
          ? format(new Date(msg.recurring_end), 'MMM d, yyyy h:mm a')
          : ''
        if (start && end) return `${pattern} from ${start} to ${end}`
        if (start) return `${pattern} from ${start}`
        if (end) return `${pattern} until ${end}`
        return pattern
      default:
        return msg.schedule_type
    }
  }
  
  const filteredMessages = messages?.filter((msg: any) => {
    if (!search) return true
    const searchLower = search.toLowerCase()
    return (
      msg.body?.toLowerCase().includes(searchLower) ||
      msg.subject?.toLowerCase().includes(searchLower)
    )
  })
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Messages</h1>
        <Link to="/messages/new" className="btn btn-primary flex items-center gap-2">
          <Plus className="w-5 h-5" />
          New Message
        </Link>
      </div>
      
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search messages..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
            />
          </div>
          
          <div className="flex gap-2">
            <select
              value={status}
              onChange={(e) => setSearchParams(e.target.value ? { status: e.target.value } : {})}
              className="input w-auto"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
            </select>
            
            <select
              value={type}
              onChange={(e) => setSearchParams(e.target.value ? { type: e.target.value } : {})}
              className="input w-auto"
            >
              <option value="">All Types</option>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
              <option value="mms">MMS</option>
            </select>
          </div>
        </div>
        
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : filteredMessages && filteredMessages.length > 0 ? (
          <div className="space-y-3">
            {filteredMessages.map((msg: any) => {
              const TypeIcon = typeIcons[msg.type as keyof typeof typeIcons] || MessageSquare
              const StatusIcon = statusIcons[msg.status as keyof typeof statusIcons] || Clock
              
              return (
                <div key={msg.id} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <TypeIcon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">{msg.subject || msg.body?.substring(0, 40)}</p>
                      <p className="text-sm text-gray-500">
                        {msg.type.toUpperCase()} • {msg.schedule_type} • {getScheduleDescription(msg)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className={`flex items-center gap-1 px-3 py-1 rounded-full ${
                      msg.status === 'sent' ? 'bg-green-100 text-green-700' :
                      msg.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      <StatusIcon className="w-4 h-4" />
                      <span className="text-sm capitalize">{msg.status}</span>
                    </div>
                    
                    <button
                      onClick={() => deleteMutation.mutate(msg.id)}
                      className="p-2 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No messages found</p>
            <Link to="/messages/new" className="text-blue-600 hover:underline mt-2 inline-block">
              Create your first message
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}
